import {
  Application,
  Container,
  Graphics,
  Particle,
  ParticleContainer,
  Sprite,
  // Sprite,
  Texture,
} from "pixi.js";

import { getProgress, stars, toPng, type Drawing } from "./db/drawing";
import { bitmaskCreate, bitmaskGet, bitmaskSet } from "./bitmask";
import type { ProcessedImage } from "./image";
import {
  createNumbersHighlight,
  createNumbersLayer,
  createOrUpdateSelectionMask,
} from "./numbers";
import type { Viewport } from "pixi-viewport";
import { attachZoomFader } from "./zoom-fade";
import { createGrayscaleWashLayer } from "./greyscale";
import { fromRgbHexValue } from "./palette";
import { createCanvasRecorder } from "./recorder";

export const tileWidth = 25;
const border = tileWidth * 2;

function xyIndex(x: number, y: number, width: number): number {
  return y * width + x;
}

export class MosaicContainer extends Container {
  private id?: string;
  public gridW = 128; // todo: make private
  public gridH = 128; // todo: make private
  /**
   * Stored HSL palette as [r, g, b] tuples
   */
  private palette: number[] = [];
  private colorGrid: Uint32Array = new Uint32Array(0); // store tint index per grid cell

  private counts: number[] = []; // parallel array of total counts per colour
  private progress: number[] = []; // parallel array of total counts
  private clicks: number[] = []; // keep a record of clicks
  private showing: Uint32Array = new Uint32Array(0); // save as booleans using a bitmask
  private hintCount = 0;

  private isPlaying = false;
  private cancelPlay = false;
  private displayNumbers = true;

  private pc: ParticleContainer;
  private numbersContainer: Container;
  private greyscaleContainer: Container;
  private numbersMask?: Sprite;

  private recorder?: ReturnType<typeof createCanvasRecorder>;
  private lastRecording?: { blob: Blob; url: string } | null;
  public autoRecord = true; // toggle if you like

  constructor(app: Application, private vp: Viewport) {
    super();
    this.x = border;
    this.y = border;

    this.greyscaleContainer = new Container();
    this.addChild(this.greyscaleContainer);
    this.setNormal();

    // The number container sits underneath
    this.numbersContainer = new Container();
    this.numbersContainer.renderable = false;
    this.addChild(this.numbersContainer);

    // the particle container sits over everything
    this.pc = new ParticleContainer({
      texture: Texture.WHITE,
      dynamicProperties: {
        tint: true,
        alpha: true,
        position: false,
        scale: false,
        rotation: false,
        uvs: false,
      },
    });
    this.pc.roundPixels = true; // optional: crisper edges at whole pixels
    this.pc.zIndex = 1000;
    this.addChild(this.pc);

    attachZoomFader(vp, this.numbersContainer, {
      tilePx: tileWidth,
    });

    vp.addChild(this);

    this.attachRecorder(app.canvas);
  }

  // whether a mosaic has been loaded
  get isLoaded() {
    return this.palette.length > 0 && this.colorGrid.length > 0;
  }

  get hints() {
    return this.hintCount;
  }

  get stars() {
    return stars(this.hintCount);
  }

  get drawingId() {
    return this.id;
  }

  getPalette() {
    return this.palette;
  }

  selectColour(idx: number) {
    this.numbersMask = createOrUpdateSelectionMask(
      this.numbersMask,
      this.gridW,
      this.gridH,
      tileWidth,
      this.colorGrid,
      idx
    );
  }

  redraw() {
    if (this.isPlaying) return;
    if (this.clicks.length === 0) return;

    this.isPlaying = true;
    this.cancelPlay = false;

    const idxToXY = (idx: number) =>
      [idx % this.gridW, Math.floor(idx / this.gridW)] as const;
    const xyToIdx = (x: number, y: number) => y * this.gridW + x;

    // --- Brush overlay setup (for the circle & the snake) ---
    const animationOverlay = new Graphics();
    if (this.sortableChildren) animationOverlay.zIndex = 9999;
    this.vp.addChild(animationOverlay);

    // --- Timing & snake visuals (single declarations) ---
    const fps = 60;
    const frameDuration = 1000 / fps;
    const snakeLen = 10;
    const minPace = 20;
    const maxPace = 30;
    const HEAD = 0xcc00cc; // purple
    const TAIL = 0xff55ff; // lighter purple

    // Helpers
    const tileCenter = (x: number, y: number) => {
      const px = x * tileWidth + this.x;
      const py = y * tileWidth + this.y;
      return {
        x: px + tileWidth / 2,
        y: py + tileWidth / 2,
      };
    };

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const lerpColor = (c1: number, c2: number, t: number) => {
      const r1 = (c1 >> 16) & 0xff,
        g1 = (c1 >> 8) & 0xff,
        b1 = c1 & 0xff;
      const r2 = (c2 >> 16) & 0xff,
        g2 = (c2 >> 8) & 0xff,
        b2 = c2 & 0xff;
      const r = Math.round(lerp(r1, r2, t));
      const g = Math.round(lerp(g1, g2, t));
      const b = Math.round(lerp(b1, b2, t));
      return (r << 16) | (g << 8) | b;
    };

    // --- Prepare ordered clicks  ---
    const clicks = [...new Set(this.clicks)]; // dedupe, keep order

    // Fast sets and pointers for commit logic
    const clickedSet = new Set<number>(clicks);
    const committedSet = new Set<number>();
    let nextClickPtr = 0; // next click index in `deduped` to commit (in order)

    // 1) Reset clicked tiles to white (unprocessed)
    this.resetToWhite();

    // 2) Build Manhattan path along click order (no diagonals)
    const path: number[] = [];
    const segmentEnds: number[] = [];
    const segmentDistances: number[] = [];

    let [curX, curY] = idxToXY(clicks[0]);
    path.push(clicks[0]);

    for (let i = 1; i < clicks.length; i++) {
      const [tx, ty] = idxToXY(clicks[i]);
      const dist = Math.abs(tx - curX) + Math.abs(ty - curY);

      const stepX = Math.sign(tx - curX);
      for (let x = curX + stepX; stepX !== 0 && x !== tx + stepX; x += stepX) {
        path.push(xyToIdx(x, curY));
      }
      curX = tx;

      const stepY = Math.sign(ty - curY);
      for (let y = curY + stepY; stepY !== 0 && y !== ty + stepY; y += stepY) {
        const idx = xyToIdx(curX, y);
        if (isNaN(idx)) {
          console.warn("NaN idx for", { curX, y, curY, ty });
        }
        path.push(idx);
      }
      curY = ty;

      segmentEnds.push(path.length);
      segmentDistances.push(Math.max(1, dist));
    }

    // 3) Pace by segment length
    let dMin = Infinity,
      dMax = -Infinity;
    for (const d of segmentDistances) {
      if (d < dMin) dMin = d;
      if (d > dMax) dMax = d;
    }
    if (!isFinite(dMin) || !isFinite(dMax)) dMin = dMax = 1;

    const norm = (d: number) =>
      dMax === dMin ? 0 : (d - dMin) / (dMax - dMin);
    const paceForSegment = (segIdx: number) => {
      if (segIdx < 0 || segIdx >= segmentDistances.length) return minPace;
      const t = norm(segmentDistances[segIdx]);
      return Math.round(lerp(minPace, maxPace, t));
    };

    // 4) Animate
    const snake: number[] = []; // indices tail..head
    let lastTime = 0;
    let pathPos = 0;
    let segIdx = 0;

    const setWhite = (idx: number) => {
      const p = this.pc.particleChildren[idx];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (p) (p as any).tint = 0xffffff;
    };
    const commitColor = (idx: number) => {
      const x = idx % this.gridW;
      const y = Math.floor(idx / this.gridW);
      this.showTile(x, y); // reveal actual mosaic color
      committedSet.add(idx);
    };
    const updateSegIdx = () => {
      while (segIdx < segmentEnds.length && pathPos >= segmentEnds[segIdx])
        segIdx++;
    };

    // Always-drawn overlay snake body (so it never "disappears" over non-click tiles)
    const drawSnakeOverlay = () => {
      animationOverlay.clear();
      if (snake.length === 0) return;

      // Draw snake body
      const n = snake.length;
      for (let i = 0; i < n; i++) {
        const idx = snake[i];
        const [x, y] = idxToXY(idx);

        const u = n === 1 ? 1 : i / (n - 1);
        const color = lerpColor(TAIL, HEAD, u);

        const px = x * tileWidth + this.x;
        const py = y * tileWidth + this.y;
        const w = tileWidth;
        const h = tileWidth;

        animationOverlay.rect(px, py, w, h).fill({ color, alpha: 0.15 });
      }
    };

    // Tail pass = only moment we revert pending clicked tiles to white
    const popTail = () => {
      const tailIdx = snake.shift()!;
      if (clickedSet.has(tailIdx) && !committedSet.has(tailIdx)) {
        setWhite(tailIdx);
      }
    };

    const stepOnce = () => {
      if (pathPos < path.length) {
        const nextIdx = path[pathPos++];
        snake.push(nextIdx);

        // Keep a fixed-length body during traversal
        if (snake.length > snakeLen) popTail();

        // Commit strictly when we arrive at the next expected clicked tile in order
        if (nextClickPtr < clicks.length && nextIdx === clicks[nextClickPtr]) {
          commitColor(nextIdx);

          // Skip contiguous duplicates of the same tile (zero-distance segments)
          const same = nextIdx;
          do {
            nextClickPtr++;
          } while (
            nextClickPtr < clicks.length &&
            clicks[nextClickPtr] === same
          );
        }

        updateSegIdx();
      } else if (snake.length > 0) {
        // Path finished: drain the body
        popTail();
      }

      // Update visuals each micro-step
      drawSnakeOverlay();
    };

    const drawHeadBrush = () => {
      // Optional: a ring around the head using the base color underneath
      if (snake.length === 0) return;
      const headIdx = snake[snake.length - 1];
      const hx = headIdx % this.gridW;
      const hy = Math.floor(headIdx / this.gridW);
      const baseColor = this.getPaletteColor(hx, hy);
      const { x: cx, y: cy } = tileCenter(hx, hy);

      const scale = this.parent?.scale?.x ?? 1;
      const size = 5;
      const radius = (size * 1.5) / scale;
      const baseWidth = 3 / scale;

      animationOverlay.circle(cx, cy, radius).stroke({
        width: baseWidth,
        color: baseColor,
        alpha: 0.9,
        alignment: 0.5,
        cap: "round",
        join: "round",
      });
    };

    setTimeout(() => {
      if (this.autoRecord && this.recorder && !this.recorder.isRecording()) {
        this.recorder.start();
      }
    }, 100); // slight delay to avoid image on first frame

    const stopRedraw = () => {
      this.isPlaying = false;
      this.emit("redrawComplete");
      setTimeout(() => {
        if (this.autoRecord && this.recorder && this.recorder.isRecording()) {
          this.recorder.stop().then((result) => {
            // Keep in memory for later share/download
            // Optionally revoke the previous one to avoid URL leaks
            if (this.lastRecording?.url)
              URL.revokeObjectURL(this.lastRecording.url);
            this.lastRecording = result;

            // (optional) fire an event so UI can show a “Share/Download” chip
            this.emit("redrawRecordingReady", {
              url: result.url,
              blob: result.blob,
            });
          });
        }
      }, 2000); // slight delay to ensure final frame is captured
    };

    const tick = (now?: number) => {
      if (this.cancelPlay) {
        // Drain with proper tail passes so pending tiles revert only as the tail passes
        while (snake.length > 0) popTail();
        animationOverlay.clear();
        this.vp.removeChild(animationOverlay);
        animationOverlay.destroy();
        stopRedraw();
        return;
      }

      if (!now) now = performance.now();
      if (now - lastTime < frameDuration) {
        requestAnimationFrame(tick);
        return;
      }
      lastTime = now;

      const currentPace =
        pathPos < path.length ? paceForSegment(segIdx) : minPace;

      for (let i = 0; i < currentPace; i++) {
        if (pathPos >= path.length && snake.length === 0) break;
        stepOnce();
      }

      // Add head ring after body so it sits on top
      drawHeadBrush();

      if (pathPos < path.length || snake.length > 0) {
        requestAnimationFrame(tick);
      } else {
        animationOverlay.clear();
        this.vp.removeChild(animationOverlay);
        animationOverlay.destroy();
        stopRedraw();
      }
    };

    lastTime = 0; // uses the single fps/frameDuration above
    requestAnimationFrame(tick);
  }

  getLastRecordingUrl() {
    return this.lastRecording?.url ?? null;
  }
  getLastRecordingBlob() {
    return this.lastRecording?.blob ?? null;
  }

  // Call this once after Pixi app is ready
  attachRecorder(canvas: HTMLCanvasElement) {
    try {
      this.recorder = createCanvasRecorder(canvas, {
        fps: 30,
      });
    } catch (e) {
      console.warn("WebM recording not available:", e);
    }
  }

  async fromDrawing(drawing: Drawing) {
    this.reset(drawing.width, drawing.height);
    this.setPalette([...drawing.palette]);
    this.colorGrid = new Uint32Array(drawing.mosaic);
    const maxReasonable = drawing.width * drawing.height;
    if (drawing.clicks.length > maxReasonable) {
      console.warn(
        `[Mosaic] Ignoring oversized clicks array (${drawing.clicks.length}), capping to ${maxReasonable}`
      );
      this.clicks = Array.from(drawing.clicks.subarray(0, maxReasonable));
    } else {
      this.clicks = Array.from(drawing.clicks);
    }
    // filter this.clicks to be in range
    this.clicks = this.clicks.filter(
      (c) => c >= 0 && c < drawing.width * drawing.height
    );
    // deduplicate while preserving order
    this.clicks = [...new Set(this.clicks)];
    this.id = drawing.id;

    // go through the drawing.showing and show the tiles
    this.showing = drawing.showing;
    for (let y = 0; y < drawing.height; y++) {
      for (let x = 0; x < drawing.width; x++) {
        // set the counts
        const paletteIndex = this.getPaletteIndex(x, y);
        if (!this.counts[paletteIndex]) {
          this.counts[paletteIndex] = 0;
        }
        this.counts[paletteIndex]++;

        if (bitmaskGet(this.showing, this.gridW, x, y)) {
          this.showTile(x, y);
          this.incProgress(paletteIndex);
        }
      }
    }
    this.hintCount = drawing.hints;
    // time to make a new numbers sprite
    await this.resetNumbers();
    this.resetGreyscale();
  }

  private async resetNumbers() {
    // destroy any existing sprites
    this.numbersContainer.removeChildren().forEach((c) => c.destroy(true));

    if (!this.displayNumbers) return;

    const result = await createNumbersLayer(this.gridW, this.gridH, {
      getValueAt: (x, y) => this.getPaletteNumber(x, y),
      tilePx: tileWidth,
    });

    this.numbersContainer.addChildAt(result, 0);
    const numbersHighlight = createNumbersHighlight(result);
    this.numbersContainer.addChild(numbersHighlight);
    this.numbersMask = createOrUpdateSelectionMask(
      undefined,
      this.gridW,
      this.gridH,
      tileWidth,
      this.colorGrid,
      0
    );
    numbersHighlight.mask = this.numbersMask;
    if (!this.numbersMask.parent) {
      this.numbersContainer.addChild(this.numbersMask);
    }
  }

  setPreview() {
    this.greyscaleContainer.alpha = 1;
    this.greyscaleContainer.filters = [];
    this.numbersContainer.renderable = false;
  }

  setNormal() {
    this.greyscaleContainer.alpha = 0.025;
    this.greyscaleContainer.filters = [];
  }

  private resetGreyscale() {
    // destroy any existing sprites
    for (const child of this.greyscaleContainer.removeChildren()) {
      child.destroy(true);
    }
    const wash = createGrayscaleWashLayer(this.gridW, this.gridH, tileWidth, {
      getRGBAt: (x, y) => this.getPaletteRGB(x, y),
    });
    this.greyscaleContainer.addChild(wash);
  }

  get computedWidth() {
    return border * 2 + this.gridW * tileWidth;
  }

  get computedHeight() {
    return border * 2 + this.gridH * tileWidth;
  }

  toDrawing(): Drawing {
    if (!this.id) {
      this.id = crypto.randomUUID();
    }

    const asInt = this.palette;

    return {
      id: this.id,
      width: this.gridW,
      height: this.gridH,
      palette: new Uint32Array(asInt),
      mosaic: new Uint8Array(this.colorGrid),
      clicks: new Uint32Array(this.clicks), // copy clicks
      showing: new Uint32Array(this.showing), // copy showing
      hints: this.hintCount,
    };
  }

  toPng(): Promise<Blob> {
    return toPng(this.toDrawing()).then(([blob]) => blob);
  }

  setId(id: string) {
    if (this.id) {
      return; // don't allow overwriting existing id
    }
    this.id = id;
  }

  async load(image: ProcessedImage, id?: string) {
    try {
      this.numbersContainer.renderable = false;
      this.reset(image.width, image.height);
      if (id) this.setId(id);
      this.setPalette(image.palette);
      this.scale.set(1);
      this.colorGrid = image.pixels;
      this.counts = new Array(this.palette.length);
      for (let i = 0; i < this.colorGrid.length; i++) {
        const idx = this.colorGrid[i];
        if (!this.counts[idx]) {
          this.counts[idx] = 1;
        } else {
          this.counts[idx]++;
        }
      }
      // time to make a new number sprite
      await this.resetNumbers();
      this.resetGreyscale();

      // flood fill the background
      this.autofillBackground();
    } catch (err) {
      console.error(err);
    }
  }

  private loadingOverlay?: ParticleContainer;
  private loadingTimer?: ReturnType<typeof setInterval> | null;

  renderLoading(isLoading: boolean) {
    // Tear down if turning off
    if (!isLoading) {
      if (this.loadingTimer) {
        clearInterval(this.loadingTimer);
        this.loadingTimer = null;
      }
      if (this.loadingOverlay) {
        this.vp.removeChild(this.loadingOverlay);
        this.loadingOverlay.destroy({ children: true });
        this.loadingOverlay = undefined;
      }
      return;
    }

    // If already showing, do nothing
    if (this.loadingOverlay) return;

    // Create an overlay ParticleContainer so we don't touch the real pixels
    const overlay = new ParticleContainer({
      texture: Texture.WHITE,
      dynamicProperties: {
        tint: true,
        alpha: true,
        position: false,
        scale: false,
        rotation: false,
        uvs: false,
      },
    });
    overlay.eventMode = "none"; // don't intercept pointer events
    overlay.roundPixels = true;
    overlay.zIndex = 5000; // below animationOverlay (9999), above mosaic
    overlay.x = this.x; // align with MosaicContainer position
    overlay.y = this.y;

    // Fill it with same grid of particles
    const whiteTex = Texture.WHITE;
    for (let y = 0; y < this.gridH; y++) {
      for (let x = 0; x < this.gridW; x++) {
        overlay.addParticle(
          new Particle({
            texture: whiteTex,
            width: tileWidth,
            height: tileWidth,
            x: x * tileWidth,
            y: y * tileWidth,
            scaleX: tileWidth,
            scaleY: tileWidth,
            alpha: 1,
          })
        );
      }
    }

    // Helper to (re)paint all tiles with a random grey
    const repaint = () => {
      const parts = overlay.particleChildren;
      // Avoid pure black/white; keep it subtle
      for (let i = 0; i < parts.length; i++) {
        const shade = (Math.random() * 140 + 60) | 0; // 60..200
        const color = (shade << 16) | (shade << 8) | shade;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (parts[i] as any).tint = color;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (parts[i] as any).alpha = 1;
      }
      overlay.update();
    };

    // First paint immediately, then every 500ms
    repaint();
    this.loadingTimer = setInterval(repaint, 100);

    // Add on the viewport (true overlay) so it doesn’t affect your main container
    // and scales/zooms with it.
    this.vp.addChild(overlay);
    this.loadingOverlay = overlay;
  }

  private bgColour(): number | undefined {
    // try and guess the background colour by sampling the borders for the most common colour
    const colorCount: Record<number, number> = {};
    const addColor = (c: number) => {
      if (!colorCount[c]) colorCount[c] = 0;
      colorCount[c]++;
    };
    // top + bottom rows
    for (let x = 0; x < this.gridW; x++) {
      addColor(this.getPaletteIndex(x, 0));
      addColor(this.getPaletteIndex(x, this.gridH - 1));
    }
    // left + right cols
    for (let y = 0; y < this.gridH; y++) {
      addColor(this.getPaletteIndex(0, y));
      addColor(this.getPaletteIndex(this.gridW - 1, y));
    }

    let bestColor = 0;
    let bestCount = 0;
    for (const c in colorCount) {
      if (colorCount[c] > bestCount) {
        bestCount = colorCount[c];
        bestColor = Number(c);
      }
    }

    // Now only assume it's a background if it covers at least 50% of the border
    const borderSize = this.gridW * 2 + this.gridH * 2;
    if (bestCount < borderSize * 0.5) return undefined;

    return bestColor;
  }

  private autofillBackground() {
    const bg = this.bgColour();
    if (bg === -1) return; // no white in palette
    // Go around the border flood fill white
    const visited = new Uint8Array(this.gridW * this.gridH);
    for (let x = 0; x < this.gridW; x++) {
      if (this.getPaletteIndex(x, 0) === bg) {
        this.floodFill(x, 0, bg, visited);
      }
    }
    for (let x = this.gridW - 1; x >= 0; x--) {
      if (this.getPaletteIndex(x, this.gridH - 1) === bg) {
        this.floodFill(x, this.gridH - 1, bg, visited);
      }
    }
    for (let y = 0; y < this.gridH; y++) {
      if (this.getPaletteIndex(0, y) === bg) {
        this.floodFill(0, y, bg, visited);
      }
    }
    for (let y = this.gridH - 1; y >= 0; y--) {
      if (this.getPaletteIndex(this.gridW - 1, y) === bg) {
        this.floodFill(this.gridW - 1, y, bg, visited);
      }
    }
  }

  private inBounds(x: number, y: number) {
    return x >= 0 && x < this.gridW && y >= 0 && y < this.gridH;
  }

  private floodFill(
    sx: number,
    sy: number,
    targetIdx: number,
    visited = new Uint8Array(this.gridW * this.gridH)
  ): Uint8Array {
    // bounds + color gate on the seed
    if (!this.inBounds(sx, sy)) return visited;
    if (this.getPaletteIndex(sx, sy) !== targetIdx) return visited;

    const w = this.gridW;

    // LIFO stack (DFS). Use an array for speed in TS.
    const stack: Array<[number, number]> = [[sx, sy]];

    while (stack.length) {
      const [x, y] = stack.pop()!;
      if (!this.inBounds(x, y)) continue;

      const vi = y * w + x;
      if (visited[vi]) continue;
      visited[vi] = 1;
      // must be the same palette index
      if (this.getPaletteIndex(x, y) !== targetIdx) continue;
      // fill this cell
      this.show(x, y); // reveal mosaic tile
      // push neighbors (4-way)
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }

    return visited;
  }

  reset(w: number, h: number) {
    this.pc.removeParticles();
    this.gridW = w;
    this.gridH = h;
    this.colorGrid = new Uint32Array(w * h); // reset tint grid
    this.palette = [];
    this.counts = [];
    this.progress = [];
    this.clicks = []; // reset clicks
    this.id = undefined; // reset id
    this.hintCount = 0;
    this.cancelPlay = true;
    this.isPlaying = false; // cancel any playing
    this.lastRecording = null;

    // why >>> 5? Because we store each bit as a boolean in a 32-bit integer
    // and 2^5 is 32
    this.showing = bitmaskCreate(w, h); // reset showing bitmask
    const whiteTex = Texture.WHITE;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        this.pc.addParticle(
          new Particle({
            texture: whiteTex,
            width: tileWidth,
            height: tileWidth,
            x: x * tileWidth,
            y: y * tileWidth,
            scaleX: tileWidth,
            scaleY: tileWidth,
            alpha: 0,
          })
        );
      }
    }
  }

  resetToWhite() {
    const clickedSet = new Set<number>(this.clicks);
    for (let y = 0; y < this.gridH; y++) {
      for (let x = 0; x < this.gridW; x++) {
        const idx = xyIndex(x, y, this.gridW);
        if (clickedSet.has(idx)) {
          const p = this.pc.particleChildren[idx];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (p) (p as any).tint = 0xffffff;
        }
      }
    }
  }

  restart() {
    // soft restart, keep the current picture
    this.resetToWhite();
    this.cancelPlay = true;
    this.isPlaying = false;
    this.showing = bitmaskCreate(this.gridW, this.gridH);
    this.progress = new Array(this.palette.length);
    this.clicks = [];
    this.hintCount = 0;
  }

  setPalette(pal: Array<number>) {
    this.palette = pal;
    this.counts = new Array(pal.length);
    this.progress = new Array(pal.length);
  }

  getPaletteIndex(x: number, y: number): number {
    const index = xyIndex(x, y, this.gridW);
    if (index < 0 || index >= this.colorGrid.length) {
      return 0; // default to first color if out of bounds
    }
    return this.colorGrid[index];
  }

  getPaletteNumber(x: number, y: number): number {
    return this.getPaletteIndex(x, y) + 1;
  }

  getPaletteColor(x: number, y: number): number {
    return this.getPaletteColorByIndex(this.getPaletteIndex(x, y));
  }

  getPaletteRGB(x: number, y: number): [number, number, number] {
    const idx = this.getPaletteIndex(x, y);
    const color = this.getPaletteColorByIndex(idx);
    return fromRgbHexValue(color);
  }

  getPaletteColorByIndex(index: number): number {
    if (index < 0 || index >= this.palette.length) {
      return 0; // default to black if out of bounds
    }
    return this.palette[index];
  }

  getUncolouredInBlock(
    paletteIdx: number,
    x: number,
    y: number,
    block: number
  ): Array<number> {
    const matches: number[] = [];
    for (let i = 0; i < block; i++) {
      for (let j = 0; j < block; j++) {
        const coordX = x + i;
        const coordY = y + j;
        // skip if outside the grid
        if (
          coordX < 0 ||
          coordX >= this.gridW ||
          coordY < 0 ||
          coordY >= this.gridH
        ) {
          continue;
        }
        const coordIdx = xyIndex(coordX, coordY, this.gridW);
        if (
          this.colorGrid[coordIdx] === paletteIdx &&
          !this.isShown(coordX, coordY)
        ) {
          matches.push(coordIdx);
        }
      }
    }
    return matches;
  }

  isShown(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.gridW || y >= this.gridH) return false;

    return bitmaskGet(this.showing, this.gridW, x, y);
  }

  isClickable(): boolean {
    return this.numbersContainer.alpha > 0.2;
  }

  click(x: number, y: number): void {
    // make sure in range
    if (x < 0 || y < 0 || x >= this.gridW || y >= this.gridH) return;

    // no preview
    this.setNormal();
    this.show(x, y);
    this.clicks.push(xyIndex(x, y, this.gridW)); // record the click
  }

  show(x: number, y: number): void {
    // check if it's already shown
    if (this.isShown(x, y)) return;

    if (x < 0 || y < 0 || x >= this.gridW || y >= this.gridH) return;

    this.showTile(x, y);

    // set the bit for this tile
    bitmaskSet(this.showing, this.gridW, x, y);

    const paletteIndex = this.getPaletteIndex(x, y);
    this.incProgress(paletteIndex);
  }

  private incProgress(idx: number): void {
    if (!this.progress[idx]) {
      this.progress[idx] = 1;
    } else {
      this.progress[idx]++;
    }
  }

  private showTile(x: number, y: number): void {
    // Show the particle at the specified grid coordinates
    const index = xyIndex(x, y, this.gridW);
    if (index < 0 || index >= this.pc.particleChildren.length) {
      return; // out of bounds
    }
    const particle = this.pc.particleChildren[index];
    if (particle) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (particle as any).tint = this.getPaletteColor(x, y);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (particle as any).alpha = 1;
    }
    this.pc.update();
  }

  showAll(): void {
    for (let y = 0; y < this.gridH; y++) {
      for (let x = 0; x < this.gridW; x++) {
        this.show(x, y);
      }
    }
  }

  getProgress(idx: number): number {
    if (!this.counts[idx]) {
      return 1; // full progress if no counts
    }
    return (this.progress[idx] ?? 0) / this.counts[idx];
  }

  getTotalProgress(): number {
    const sumCounts = this.counts.reduce((a, b) => a + b, 0);
    const sumProgress = this.progress.reduce((a, b) => a + b, 0);
    return sumProgress / sumCounts;
  }

  getTotalProgressClicks(): number {
    const sumProgress = this.progress.reduce((a, b) => a + b, 0);
    return getProgress(
      this.gridW * this.gridH,
      sumProgress,
      this.clicks.length
    );
  }

  isStarted(): boolean {
    return this.clicks.length > 0;
  }

  incrementHintCount() {
    this.hintCount++;
  }
}
